import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSubscription } from '@/hooks/useSubscription';
import { usePaperThemeActive } from '@/hooks/useThemeFlag';
import { Titles } from '@/lib/navigation';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import { useBillingHistory } from '@/hooks/useBillingHistory';
import { PaymentMethodList } from '@/components/PaymentMethodList';
import { PaymentHistory } from '@/components/PaymentHistory';
import { CurrentPlanPill, EndingPlanPill, StartingPlanPill } from '../labels/createPill';
import { CancelConfirmModal, PlanChangeConfirmModal } from './BillingConfirmModals';
import { SettingsShell } from './SettingsShell';
import { BillingSettingsPaper } from '../paper/BillingSettingsPaper';
import {
  trackPlanUpgradeClicked,
  trackPlanDowngradeClicked,
  trackPlanChangeConfirmed,
  trackSubscriptionCancelled,
  trackSubscriptionResumed,
  trackManageSubscriptionClicked,
} from '@/analytics';

interface Plan {
  id: string;
  name: string;
  price: number;
  description: string;
}

const plans: Plan[] = [
  { id: 'free', name: 'Free', price: 0, description: 'Basic access with limited usage budget. Ideal for exploring the platform.' },
  { id: 'tier1', name: 'Tier 1', price: 10, description: 'Extended usage budget for regular use. Suitable for individual workflows.' },
  { id: 'tier2', name: 'Tier 2', price: 50, description: 'Significantly higher usage capacity for demanding workloads and longer sessions.' },
  { id: 'tier3', name: 'Tier 3', price: 100, description: 'Maximum usage allocation for power users and intensive, production-grade usage.' },
];

const TIER_ORDER: Record<string, number> = { free: 0, tier1: 1, tier2: 2, tier3: 3 };

export const BillingSettingsView = () => {
  const isPaper = usePaperThemeActive();
  if (isPaper) return <BillingSettingsPaper />;
  return <BillingSettingsLegacy />;
};

const BillingSettingsLegacy = () => {
  useDocumentTitle(Titles.billing());
  const {
    currentPlanId, isActive, isCanceling, isDowngrading,
    pendingPlanId: scheduledPlanId, currentPeriodEnd,
    loading: subLoading, error: subError,
    checkout, openPortal, cancelSubscription, resumeSubscription, previewProration,
  } = useSubscription();
  const { methods: paymentMethods, error: pmError } = usePaymentMethods();
  const { events: historyEvents } = useBillingHistory();

  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [prorationPreview, setProrationPreview] = useState<{ immediateChargeUsd: number; currency: string } | null>(null);
  const [prorationLoading, setProrationLoading] = useState(false);

  const currentTierOrder = TIER_ORDER[currentPlanId] ?? 0;

  const handlePlanChangeClick = async (planId: string) => {
    const plan = plans.find((p) => p.id === planId);
    const planOrder = TIER_ORDER[planId] ?? 0;
    const isDowngradeAction = planOrder < currentTierOrder;
    if (isDowngradeAction) {
      trackPlanDowngradeClicked(planId, plan?.price ?? 0);
    } else {
      trackPlanUpgradeClicked(planId, plan?.price ?? 0);
    }

    if (!isActive) {
      setCheckoutLoading(planId);
      try {
        await checkout(planId);
      } finally {
        setCheckoutLoading(null);
      }
      return;
    }

    setPendingPlanId(planId);
    setProrationPreview(null);
    if (!isDowngradeAction) {
      setProrationLoading(true);
      try {
        const preview = await previewProration(planId);
        setProrationPreview(preview);
      } catch {
        // silently fall back to generic message
      } finally {
        setProrationLoading(false);
      }
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!pendingPlanId) return;
    const planId = pendingPlanId;
    const plan = plans.find((p) => p.id === planId);
    trackPlanChangeConfirmed(planId, plan?.price ?? 0);
    setPendingPlanId(null);
    setCheckoutLoading(planId);
    try {
      await checkout(planId);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    trackManageSubscriptionClicked();
    await openPortal();
  };

  const handleResumeSubscription = async () => {
    trackSubscriptionResumed();
    setResumeLoading(true);
    try {
      await resumeSubscription();
    } finally {
      setResumeLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    trackSubscriptionCancelled();
    setCancelLoading(true);
    try {
      await cancelSubscription();
    } finally {
      setCancelLoading(false);
      setShowCancelConfirm(false);
    }
  };

  const handleKeepCurrentPlan = async () => {
    setCheckoutLoading(currentPlanId);
    try {
      await checkout(currentPlanId);
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <SettingsShell activeTab="billing">
      <div className="mt-6">
        <h3 className="mb-1 text-[14px] font-medium text-zinc-200">Plans</h3>
        <p className="mb-4 text-[13px] text-zinc-500">
          Each tier provides a higher monthly inference budget, giving you more capacity for agent interactions and longer sessions.
        </p>

        {subError && (
          <div className="mb-4 rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-[13px] text-red-400">
            {subError}
          </div>
        )}

        {isActive && currentPeriodEnd && !isCanceling && !isDowngrading && (
          <div className="mb-4 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-[13px] text-emerald-400">
            Next billing date: {new Date(currentPeriodEnd * 1000).toLocaleDateString()}
          </div>
        )}

        {isActive && currentPeriodEnd && isCanceling && (
          <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-[13px] text-amber-400">
            Your subscription will end on {new Date(currentPeriodEnd * 1000).toLocaleDateString()}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {plans.map((plan) => {
            const isFree = plan.id === 'free';
            const isCurrentPlan = (isActive && plan.id === currentPlanId) || (isFree && !isActive);
            const isScheduledTarget = isDowngrading && plan.id === scheduledPlanId;
            const wasPreviousPlan = !isActive && plan.id === currentPlanId && currentPlanId !== 'free';
            const planOrder = TIER_ORDER[plan.id] ?? 0;
            const isUpgrade = isActive && planOrder > currentTierOrder;
            const isDowngrade = isActive && planOrder < currentTierOrder;
            const isCheckingOut = checkoutLoading === plan.id;

            return (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrentPlan={isCurrentPlan}
                isScheduledTarget={isScheduledTarget}
                wasPreviousPlan={wasPreviousPlan}
                isUpgrade={isUpgrade}
                isDowngrade={isDowngrade}
                isCheckingOut={isCheckingOut}
                isFree={isFree}
                isCanceling={isCanceling}
                isDowngrading={isDowngrading}
                currentPeriodEnd={currentPeriodEnd}
                subLoading={subLoading}
                cancelLoading={cancelLoading}
                resumeLoading={resumeLoading}
                checkoutLoading={checkoutLoading}
                currentPlanId={currentPlanId}
                onPlanChangeClick={handlePlanChangeClick}
                onShowCancelConfirm={() => setShowCancelConfirm(true)}
                onResume={handleResumeSubscription}
                onKeepCurrentPlan={handleKeepCurrentPlan}
              />
            );
          })}
        </div>

        <div className="mt-8">
          <PaymentMethodList
            methods={paymentMethods}
            onManage={handleManageSubscription}
            error={!!pmError}
          />
        </div>

        <div className="mt-8">
          <PaymentHistory events={historyEvents} />
        </div>
      </div>

      {showCancelConfirm && (
        <CancelConfirmModal
          currentPeriodEnd={currentPeriodEnd}
          cancelLoading={cancelLoading}
          onCancel={handleCancelSubscription}
          onKeep={() => setShowCancelConfirm(false)}
        />
      )}

      {pendingPlanId && (() => {
        const pendingPlan = plans.find((p) => p.id === pendingPlanId);
        if (!pendingPlan) return null;
        const pendingOrder = TIER_ORDER[pendingPlanId] ?? 0;
        return (
          <PlanChangeConfirmModal
            plan={pendingPlan}
            isPendingDowngrade={pendingOrder < currentTierOrder}
            currentPeriodEnd={currentPeriodEnd}
            isDowngrading={isDowngrading}
            isCanceling={isCanceling}
            prorationLoading={prorationLoading}
            prorationPreview={prorationPreview}
            onConfirm={handleConfirmPlanChange}
            onCancel={() => { setPendingPlanId(null); setProrationPreview(null); }}
          />
        );
      })()}
    </SettingsShell>
  );
};

/* ------------------------------------------------------------------ */
/*  Plan card                                                          */
/* ------------------------------------------------------------------ */

interface PlanCardProps {
  plan: Plan;
  isCurrentPlan: boolean;
  isScheduledTarget: boolean;
  wasPreviousPlan: boolean;
  isUpgrade: boolean;
  isDowngrade: boolean;
  isCheckingOut: boolean;
  isFree: boolean;
  isCanceling: boolean;
  isDowngrading: boolean;
  currentPeriodEnd: number | null;
  subLoading: boolean;
  cancelLoading: boolean;
  resumeLoading: boolean;
  checkoutLoading: string | null;
  currentPlanId: string;
  onPlanChangeClick: (planId: string) => void;
  onShowCancelConfirm: () => void;
  onResume: () => void;
  onKeepCurrentPlan: () => void;
}

const PlanCard = (props: PlanCardProps) => {
  const action = getPlanAction(props);

  return (
    <div
      className={cn(
        'rounded-xl border p-4 transition-colors',
        props.isCurrentPlan && (props.isCanceling || props.isDowngrading)
          ? 'border-amber-500/30 bg-amber-500/5'
          : props.isCurrentPlan || props.isScheduledTarget || (props.isFree && props.isCanceling)
            ? 'border-emerald-500/30 bg-emerald-500/5'
            : 'border-white/[0.06] bg-white/[0.02]',
      )}
    >
      <div className="flex items-center gap-2">
        <h4 className="text-[14px] font-medium text-zinc-200">{props.plan.name}</h4>
        {props.isCurrentPlan && (props.isCanceling || props.isDowngrading) && props.currentPeriodEnd && (
          <EndingPlanPill label={`Ending ${formatDateSlash(props.currentPeriodEnd)}`} />
        )}
        {props.isCurrentPlan && !props.isCanceling && !props.isDowngrading && (
          <CurrentPlanPill label="Current" />
        )}
        {props.isScheduledTarget && props.currentPeriodEnd && (
          <StartingPlanPill label={`Starting ${formatDateSlash(props.currentPeriodEnd)}`} />
        )}
        {props.isFree && props.isCanceling && props.currentPeriodEnd && (
          <StartingPlanPill label={`Starting ${formatDateSlash(props.currentPeriodEnd)}`} />
        )}
      </div>

      <p className="mt-1">
        <span className="text-[20px] font-medium text-zinc-100">${props.plan.price}</span>
        {props.plan.price > 0 && (
          <span className="text-[13px] text-zinc-500"> / month</span>
        )}
      </p>

      <p className="mt-2 text-[12px] leading-relaxed text-zinc-500">{props.plan.description}</p>

      {props.subLoading ? (
        <div className="mt-4 flex h-9 w-full items-center justify-center rounded-lg border border-white/[0.08]">
          <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
        </div>
      ) : action}
    </div>
  );
};

const formatDateSlash = (ts: number) =>
  new Date(ts * 1000).toISOString().split('T')[0].replace(/-/g, '/');

const getPlanAction = (props: PlanCardProps) => {
  const { isCurrentPlan, isFree, isCanceling, isDowngrading, isScheduledTarget, wasPreviousPlan, isUpgrade, isDowngrade, isCheckingOut } = props;

  if (isCurrentPlan && isFree) return null;

  if (isCurrentPlan && isCanceling) {
    return (
      <ActionButton variant="secondary" onClick={props.onResume} loading={props.resumeLoading}>
        Reactivate
      </ActionButton>
    );
  }

  if (isCurrentPlan && isDowngrading) {
    return (
      <ActionButton variant="primary" onClick={props.onKeepCurrentPlan} loading={props.checkoutLoading === props.currentPlanId}>
        Keep plan
      </ActionButton>
    );
  }

  if (isCanceling && isFree) return null;

  if (isCanceling) {
    return (
      <ActionButton variant="primary" onClick={() => props.onPlanChangeClick(props.plan.id)} loading={isCheckingOut} disabled={!!props.checkoutLoading}>
        Subscribe
      </ActionButton>
    );
  }

  if (isCurrentPlan) {
    return (
      <ActionButton variant="secondary" onClick={props.onShowCancelConfirm} loading={props.cancelLoading}>
        Cancel
      </ActionButton>
    );
  }

  if (wasPreviousPlan) {
    return (
      <ActionButton variant="primary" onClick={() => props.onPlanChangeClick(props.plan.id)} loading={isCheckingOut} disabled={!!props.checkoutLoading}>
        Re-subscribe
      </ActionButton>
    );
  }

  if (isUpgrade) {
    return (
      <ActionButton variant="primary" onClick={() => props.onPlanChangeClick(props.plan.id)} loading={isCheckingOut} disabled={!!props.checkoutLoading}>
        Upgrade
      </ActionButton>
    );
  }

  if (isScheduledTarget) return null;

  if (isDowngrade) {
    return (
      <ActionButton variant="secondary" onClick={() => props.onPlanChangeClick(props.plan.id)} loading={isCheckingOut} disabled={!!props.checkoutLoading}>
        Change Plan
      </ActionButton>
    );
  }

  return (
    <ActionButton variant="primary" onClick={() => props.onPlanChangeClick(props.plan.id)} loading={isCheckingOut} disabled={!!props.checkoutLoading}>
      Subscribe
    </ActionButton>
  );
};

/* ------------------------------------------------------------------ */
/*  Shared action button                                               */
/* ------------------------------------------------------------------ */

interface ActionButtonProps {
  variant: 'primary' | 'secondary';
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}

const ActionButton = ({ variant, onClick, loading, disabled, children }: ActionButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    className={cn(
      'mt-4 flex h-9 w-full items-center justify-center rounded-lg text-[13px] font-medium transition-colors disabled:opacity-50',
      variant === 'primary'
        ? 'bg-white text-zinc-900 hover:opacity-90'
        : 'border border-white/[0.08] text-zinc-400 hover:bg-white/[0.03]',
    )}
  >
    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
  </button>
);
